import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccessDenied from "../../../components/access_denied/access_denied";
import CmsLayout from "../../../layouts/cms/cms_layout";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { deleteBanner, getBannerList } from "../../../services/cms/cms";
import BannerListing, { StatusFilter } from "../components/banner_listing/banner_listing";
import DeleteCmsItemDialog from "../components/delete_cms_item/delete_cms_item";
import FilterBanners, { BannersFilterValues } from "../components/filter_banners/filter_banners";
import SortBannersPopup, {
  DEFAULT_BANNERS_SORT_OPTION,
  BannersSortOption,
  bannersSortOptionLabel,
  bannersSortOptionToParams,
} from "../components/sort_banners_popup/sort_banners_popup";
import { Banner, BannerType } from "../models/cms";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";

const ROWS_PER_PAGE = 10;

export default function BannersPage() {
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [bannerType, setBannerType] = useState<BannerType | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<BannersSortOption>(DEFAULT_BANNERS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, statusFilter, bannerType, dateFrom, dateTo, selectedOrgId, sortOption]);

  const fromDate = dateFrom?.format("YYYY-MM-DD");
  const toDate = dateTo?.format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery(
    ["cms-banners", page, debouncedSearchValue, statusFilter, bannerType, selectedOrgId, sortOption, fromDate, toDate],
    async () => {
      const { sortBy, sortOrder } = bannersSortOptionToParams(sortOption);
      const response = await getBannerList({
        page,
        size: ROWS_PER_PAGE,
        search: debouncedSearchValue,
        status: statusFilter === "all" ? undefined : statusFilter,
        bannerType,
        orgId: selectedOrgId || undefined,
        sortBy,
        sortOrder,
        fromDate,
        toDate,
      });
      return response?.data?.data;
    },
    { keepPreviousData: true }
  );

  const list: Banner[] = data?.list || [];
  const count = data?.total || 0;

  const filterCount = [statusFilter !== "all", !!bannerType, !!dateFrom && !!dateTo].filter(Boolean).length;

  const handleApplyFilters = (values: BannersFilterValues) => {
    setStatusFilter(values.statusFilter);
    setBannerType(values.bannerType);
    setDateFrom(values.dateFrom);
    setDateTo(values.dateTo);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteBanner(deleteTarget.id)
      .then(() => {
        showSnackbar("Banner deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-banners"] });
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteTarget(null);
      });
  };

  return (
    <MainLayout>
      {hasPermission("cms", "read") ? (
        <CmsLayout>
          <BannerListing
            list={list}
            count={count}
            page={page}
            setPage={setPage}
            isLoading={isLoading || isFetching}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filterCount={filterCount}
            onOpenFilters={() => setFilterDialogOpen(true)}
            sortLabel={bannersSortOptionLabel(sortOption)}
            onOpenSort={() => setSortDialogOpen(true)}
            onDelete={setDeleteTarget}
          />
          <DeleteCmsItemDialog
            open={!!deleteTarget}
            itemName={deleteTarget?.title}
            isDeleting={isDeleting}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
          {filterDialogOpen && (
            <FilterBanners
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              statusFilter={statusFilter}
              bannerType={bannerType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={handleApplyFilters}
            />
          )}
          <SortBannersPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </CmsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
